<template>
  <VMenu
    :value="isMenuOpen"
    class="DatePicker"
    :close-on-content-click="!range"
    transition="scale-transition"
    min-width="320px"
    @input="isMenuOpen = !isMenuOpen"
  >
    <template #activator="{ on }">
      <VBtn
        :style="btnStyle"
        v-bind="activatorBind"
        v-on="on"
        @click="$emit('update:isOpen', true)"
      >
        <VIcon
          left
          v-text="'mdi-calendar'"
        />{{ textDisplayValue }}<VIcon
          right
          v-text="isMenuOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        />
      </VBtn>
    </template>
    <VRow
      align="center"
      justify="center"
      class="white"
    >
      <VCol
        class="text-center"
        :cols="range ? '4' : '12'"
      >
        <VBtn
          text
          color="primary"
          class="text-center"
          @click="setToday()"
        >
          Сегодня
        </VBtn>
      </VCol>
      <VCol
        v-if="range"
        cols="4"
        class="text-center"
      >
        <VBtn
          text
          color="primary"
          class="text-center"
          @click="setThisWeek()"
        >
          Неделя
        </VBtn>
      </VCol>
      <VCol
        v-if="range"
        cols="4"
        class="text-center"
      >
        <VBtn
          text
          color="primary"
          @click="setThisMonth()"
        >
          Месяц
        </VBtn>
      </VCol>
    </VRow>
    <VSpacer />
    <VDatePicker
      :value="intermediateModel"
      :range="range"
      no-title
      scrollable
      :max="max"
      :locale="locale"
      :first-day-of-week="firstDayOfWeek"
      style="width: 100%"
      @input="onInput($event)"
      @change="onChange($event)"
    />
  </VMenu>
</template>

<script>
import moment from 'moment'
import * as R from 'ramda'

const ISO_DATE = 'YYYY-MM-DD'
const PRETTY_DATE = 'll'
const DAY_AND_MONTH = 'D MMM'
const toPrettyDate = dt => moment(dt).format(PRETTY_DATE)

export default {
  name: 'DatePicker',
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    // Строка в ISO: '2020-01-01'
    // с параметром range: пара строк в ISO: ['2020-01-01', '2020-01-03']
    value: { type: [String, Array], required: true },
    // Выбор двух дат, событие $input передаст пару дат, примеры:
    //   - ['2020-01-01', '2020-01-01'] // Выбор 1 даты таким образом
    //   - ['2020-01-01', '2020-01-03'] // Кликнул с 1 по 3 янв.
    //   - ['2020-01-03', '2020-01-01'] // Кликнул сначала 3 янв. потом 1 янв.
    range: { type: Boolean, default: false },
    // Дата в ISO, максимальная дата когда возможен выбор
    max: { type: String, default: null },
    locale: { type: String, default: 'ru' }, // Язык
    firstDayOfWeek: { type: Number, default: 1 }, // 0 - вс, 1 - пн
    // <DatePicker :is-open.sync="pickerIsOpen" ... />
    isOpen: { type: Boolean, default: false },
    btnStyle: { type: [Object, String], default: null },
  },
  data() {
    const { range, value } = this
    return {
      // <v-date-picker range /> требует чтобы модель по очереди собирала
      // обе даты (приходят 2 события @input сначала ['2020-01-01'],
      // потом ['2020-01-01', '2020-01-02']) и только после второго события
      // с двумя датами вызывает событие @change с парой выбранных дат
      // см. https://vuetifyjs.com/en/components/date-pickers#date-pickers-range
      // при выборе даты начала и конца
      intermediateModel: range ? [ ...value ] : value,
      isMenuOpen: this.isOpen || false,
    }
  },
  computed: {
    activatorBind() {
      const { $attrs, btnStyle: style } = this
      return {
        // Кнопка активации, attrs по-умолчанию
        color: 'primary',
        left: true,
        // Все прочие свойства компонента прокидываются в кнопку-активатор
        ...$attrs,
        style,
      }
    },
    textDisplayValue() {
      const { range, value } = this
      const dates = range ? R.uniq(value) : [ value ]
      if (dates.length === 2 && dates[0].slice(0, 7) === dates[1].slice(0, 7)) {
        return [ // две даты в 1 месяц
          moment(dates[0]).format('D'),
          toPrettyDate(dates[1]),
        ].join(' — ')
      } else if (dates.length === 2 && dates[0].slice(0, 4) === dates[1].slice(0, 4)) {
        return [ // две даты в 1 год
          moment(dates[0]).format(DAY_AND_MONTH),
          toPrettyDate(dates[1]),
        ].join(' — ')
      }
      return dates.map(toPrettyDate).join(' — ')
    },
  },
  watch: {
    range: 'updateIntermediateValue',
    value: 'updateIntermediateValue',
    isOpen(isOpen) { this.isMenuOpen = isOpen || false },
    isMenuOpen(isMenuOpen) { this.$emit('update:isOpen', isMenuOpen) },
  },
  methods: {
    updateIntermediateValue() {
      const { range, value } = this
      this.intermediateModel = range ? [ ...value ] : value
    },
    setToday() {
      const { range } = this
      const today = moment().format(ISO_DATE)
      this.$emit('input', range ? [ today, today ] : today)
      this.isMenuOpen = false
    },
    setThisWeek() {
      const { range } = this
      if (!range) {
        throw new Error('range mode required')
      }
      const now = moment()
      const start = now.startOf('week').format(ISO_DATE)
      const end = now.endOf('week').format(ISO_DATE)
      this.$emit('input', [ start, end ])
      this.isMenuOpen = false
    },
    setThisMonth() {
      const { range } = this
      if (!range) {
        throw new Error('range mode required')
      }
      const now = moment()
      const start = now.startOf('month').format(ISO_DATE)
      const end = now.endOf('month').format(ISO_DATE)
      this.$emit('input', [ start, end ])
      this.isMenuOpen = false
    },
    onInput(value) {
      const { range } = this
      this.intermediateModel = value
      if (!range) {
        this.$emit('input', value)
        this.isMenuOpen = false
      }
    },
    onChange(value) {
      const { range } = this
      if (range) {
        this.$emit('input', value)
        this.isMenuOpen = false
      }
    },
  },
}
</script>
